import { SupplierIcon } from "@/app/Suppliers/Components/SupplierIcon";
import { RecordScreen } from "@/lib/Components/Screens/RecordScreen/RecordScreen";
import {
  CalendarDaysIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import { useRelocationRecord } from "@/app/Relocations/Hooks/useRelocationRecord";
import { useGapRentalActions } from "@/app/GapRentals/Hooks/useGapRentalActions";
import { StopwatchIcon } from "@radix-ui/react-icons";
import { RelocationDraftWarning } from "@/app/Relocations/Components/RelocationDraftWarning";
import { Badge } from "@/components/catalyst/badge";
import { useTranslations } from "use-intl";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { RelocationStatusBadge } from "@/app/Relocations/Components/RelocationStatusBadge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Link } from "@/components/catalyst/link";
import { ActivityIcon, ImagesIcon, MegaphoneIcon, Star } from "lucide-react";
import { ImoovaImage } from "@/app/Media/Components/Image";

export function GapRentalRecordScreen() {
  const { hasSupplier } = useGlobalSupplier();

  const gapRental = useRelocationRecord();
  const getActions = useGapRentalActions();
  const tScreen = useTranslations("relocation.recordScreen");
  const tTabs = useTranslations("relocation.recordScreen.tabs");

  return (
    <RecordScreen
      tabParams={{ relocationId: gapRental.id }}
      title={
        <div className="flex items-center gap-2">
          <span>{gapRental.departureOffice.name}</span>
          {gapRental.priority !== null && gapRental.priority !== undefined && (
            <Star className="h-5 w-5 shrink-0 fill-yellow-400 text-yellow-400" />
          )}
        </div>
      }
      image={
        gapRental.ogImage ? (
          <div className="mr-3 size-20 overflow-hidden rounded-md">
            <ImoovaImage media={gapRental.ogImage} alt={gapRental.reference} />
          </div>
        ) : null
      }
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals"
                params={(prev) => prev}
              >
                {tScreen("breadcrumbGapRentals")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{gapRental.reference}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      }
      badges={
        <>
          <RelocationStatusBadge relocation={gapRental} />
          {gapRental.departure_dates.length === 0 ? (
            <Badge color="red">
              <ExclamationTriangleIcon className="h-3 w-3" />
              No departure dates
            </Badge>
          ) : null}
          {gapRental.lines.length > 0 ? (
            <Badge color="zinc">
              {tScreen("plate")}{" "}
              {gapRental.lines.map((r) => r.reference).join(", ")}
            </Badge>
          ) : null}

          {!hasSupplier ? (
            <Badge color="zinc">
              <SupplierIcon
                model={gapRental.supplier}
                className="size-3.5 shrink-0 rounded-sm"
              />
              <Link
                className="underline"
                from="/$tenant/$orgId/supplier/{-$supplierId}"
                to="/$tenant/$orgId/supplier/{-$supplierId}/settings"
                params={(prev) => prev}
                search={{ supplier: gapRental.supplier.id }}
              >
                {gapRental.supplier.name}
              </Link>
            </Badge>
          ) : null}
        </>
      }
      banner={<RelocationDraftWarning />}
      actionsGroups={getActions(gapRental)}
      tabs={[
        {
          name: tTabs("details"),
          Icon: DocumentTextIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals/$relocationId",
        },
        {
          name: tTabs("bookings"),
          Icon: CalendarDaysIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals/$relocationId/bookings",
        },
        {
          name: tTabs("activity"),
          Icon: ActivityIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals/$relocationId/activity",
        },
        {
          name: tTabs("images"),
          Icon: ImagesIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals/$relocationId/generations",
        },
        {
          name: tTabs("waitlists"),
          Icon: StopwatchIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals/$relocationId/waitlists",
          isAdminTab: true,
        },
        {
          name: tTabs("ads"),
          Icon: MegaphoneIcon,
          to: "/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals/$relocationId/ads",
          isAdminTab: true,
        },
      ]}
    />
  );
}
